/**
 * InfoHint — วงกลม (i) ข้างหัวเรื่อง ที่เก็บประโยคอธิบายซึ่งเคยกินที่อยู่ในตัวหน้าจอ
 * ทุกวินาทีที่เปิดหน้า (`WO-P2-M11-004`, D-107 ข้อ 5)
 *
 * **ย้ายที่ ไม่ได้ลบทิ้ง** — ข้อความยังจำเป็นตอนที่ผู้ใช้สงสัยว่าทำไมของหาย เพียงแต่
 * ไม่ต้องอยู่ในสายตาตลอดเวลา
 *
 * ต้องเปิดได้ทั้ง hover และ focus คีย์บอร์ด: trigger เป็น `<button>` จริงในลำดับ Tab
 * (ไม่ใช่ `<span title="...">` ซึ่งใช้เมาส์อย่างเดียวได้) และข้อความผูกกับปุ่มด้วย
 * `aria-describedby` เพื่อให้ screen reader ไปถึงได้โดยไม่ต้องเปิด popup ก่อน —
 * บับเบิลจึงอยู่ใน DOM ตลอด แค่ซ่อน/โชว์ด้วย CSS (ไม่ใช้ `hidden`/`display:none`
 * ซึ่งจะตัด `aria-describedby` ออกจาก accessibility tree ไปด้วย)
 */

import { useId, useState, type ReactNode } from 'react';

export interface InfoHintProps {
  /** ประโยคอธิบายที่ย้ายออกมาจากตัวหน้า (ReactNode เพื่อรองรับหลายย่อหน้า/ตัวหนา — D-119) */
  readonly text: ReactNode;
  /** ชื่อที่ screen reader ประกาศสำหรับปุ่มนี้ */
  readonly label?: string;
}

export function InfoHint({ text, label = 'คำอธิบายเพิ่มเติม' }: InfoHintProps) {
  const [open, setOpen] = useState(false);
  const bubbleId = useId();

  const show = () => {
    setOpen(true);
  };
  const hide = () => {
    setOpen(false);
  };

  return (
    <span className="cap-info-hint">
      <button
        type="button"
        className="cap-info-hint__trigger"
        aria-label={label}
        aria-describedby={bubbleId}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <span aria-hidden="true">ⓘ</span>
      </button>
      <span
        role="tooltip"
        id={bubbleId}
        className={`cap-info-hint__bubble${open ? ' cap-info-hint__bubble--open' : ''}`}
      >
        {text}
      </span>
    </span>
  );
}
